import { useContext, useState } from 'react';
import AuthContext from '../../store/auth-context';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import {
    createFacility,
    updateFacility,
    type CreateFacilityInput,
    type UpdateFacilityInput,
} from '../../services/api/facilityService';
import {
    updateFacilityInfo,
    type UpdateFacilityInfoInput,
} from '../../services/api/onboardingService';
import { addDays, formatDateInputValue, type Facility } from './utils/utils';
import type { TFunction } from 'i18next';

function defaultStartDate(): string {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return formatDateInputValue(d);
}

export interface AddressValues {
    street: string;
    number: string;
    postalCode: string;
    city: string;
    country: string;
}

export interface FacilityFormValues {
    name: string;
    licenses: number;
    region: string;
    featureAccess: string[];
    startDate: string;
    endDate: string;
    isTrial: boolean;
    address: AddressValues;
}

interface EditFacilityFormProps {
    mode: 'create' | 'edit';
    departmentId?: string;
    initialValues?: Partial<FacilityFormValues>;
    onSaved?: (facility?: Facility) => void;
    submitLabel?: string;
    hideSubmit?: boolean;
    saveMode: 'admin' | 'siteAdmin';
    allowSubmitWithoutChanges?: boolean;
}

interface SiteAdminFacilityUpdate {
    hasChanges: boolean;
    payload: UpdateFacilityInfoInput;
}

function createDefaultFormValues(
    initialValues?: Partial<FacilityFormValues>
): FacilityFormValues {
    const startDate = defaultStartDate();
    const address: AddressValues = {
        street: '',
        number: '',
        postalCode: '',
        city: '',
        country: '',
        ...initialValues?.address,
    };

    return {
        name: '',
        licenses: 0,
        region: '',
        featureAccess: [],
        startDate,
        endDate: addDays(startDate, 30),
        isTrial: true,
        ...initialValues,
        address,
    };
}

function buildCreateFacilityInput(
    form: FacilityFormValues
): CreateFacilityInput {
    return {
        name: form.name.trim(),
        licenses: form.licenses,
        isTrial: form.isTrial,
        region: form.region.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        featureAccess: form.featureAccess,
    };
}

function buildSiteAdminFacilityUpdate(
    form: FacilityFormValues,
    defaults: FacilityFormValues
): SiteAdminFacilityUpdate {
    const hasNameChange = form.name !== defaults.name;
    const hasAddressChange =
        JSON.stringify(form.address) !== JSON.stringify(defaults.address);

    return {
        hasChanges: Boolean(hasNameChange || hasAddressChange),
        payload: {
            ...(hasNameChange ? { name: form.name.trim() } : {}),
            ...(hasAddressChange
                ? {
                      address: {
                          street: form.address.street.trim() || undefined,
                          number: form.address.number.trim() || undefined,
                          postalCode: parseInt(form.address.postalCode, 10),
                          city: form.address.city.trim(),
                          country: form.address.country.trim(),
                      },
                  }
                : {}),
        },
    };
}

function getValidationMessage(
    t: TFunction,
    {
        missingName,
        invalidPostalCode,
        isCreate,
    }: {
        missingName: boolean;
        invalidPostalCode: boolean;
        isCreate: boolean;
    }
): { title: string; description: string } {
    if (invalidPostalCode) {
        return {
            title: t('facilities.editForm.validation.invalidPostalCodeTitle'),
            description: t(
                'facilities.editForm.validation.invalidPostalCodeDesc'
            ),
        };
    }
    if (missingName) {
        return {
            title: t('facilities.editForm.validation.missingFieldsTitle'),
            description: t('facilities.editForm.validation.missingNameDesc'),
        };
    }
    return {
        title: t('facilities.editForm.validation.missingFieldsTitle'),
        description: isCreate
            ? t('facilities.editForm.validation.missingCreateDesc')
            : t('facilities.editForm.validation.missingEditDesc'),
    };
}

function buildAdminFacilityUpdates(
    form: FacilityFormValues,
    defaults: FacilityFormValues
): UpdateFacilityInput {
    const updates: UpdateFacilityInput = {};

    if (form.name !== defaults.name) updates.name = form.name.trim();
    if (form.region !== defaults.region) updates.region = form.region.trim();
    if (form.licenses !== defaults.licenses) updates.licenses = form.licenses;
    if (form.startDate !== defaults.startDate)
        updates.start_date = form.startDate;
    if (form.endDate !== defaults.endDate) updates.end_date = form.endDate;
    if (form.isTrial !== defaults.isTrial) updates.isTrial = form.isTrial;
    if (
        JSON.stringify([...form.featureAccess].sort()) !==
        JSON.stringify([...defaults.featureAccess].sort())
    )
        updates.featureAccess = form.featureAccess;

    return updates;
}

export default function EditFacilityForm({
    mode,
    departmentId,
    initialValues,
    onSaved,
    submitLabel,
    hideSubmit,
    saveMode,
    allowSubmitWithoutChanges = false,
}: EditFacilityFormProps) {
    const { toast } = useToast();
    const { t } = useTranslation();
    const isCreate = mode === 'create';
    const isSiteAdminEdit = saveMode === 'siteAdmin' && !isCreate;
    const showAdminFields = !isSiteAdminEdit;

    const featureOptions = [
        { key: 'CPTReporting', label: 'CPT Reporting' },
        { key: 'GuestAccess', label: 'Guest Access' },
    ] as const;

    // On a regional stack every department carries that stack's region; the
    // server rejects anything else, so the field is shown read-only.
    const { stackRegion } = useContext(AuthContext);
    const lockedRegion = stackRegion?.toUpperCase();
    const [defaults] = useState(() =>
        createDefaultFormValues({
            ...(lockedRegion ? { region: lockedRegion } : {}),
            ...initialValues,
        })
    );
    const [form, setForm] = useState<FacilityFormValues>(defaults);
    const [loading, setLoading] = useState(false);

    const hasChanges =
        isCreate || JSON.stringify(form) !== JSON.stringify(defaults);

    const buttonLabel = loading
        ? isCreate
            ? t('facilities.editForm.creating')
            : t('facilities.editForm.saving')
        : (submitLabel ??
          (isCreate
              ? t('facilities.editForm.createFacility')
              : t('facilities.editForm.saveChanges')));

    const update = <K extends keyof FacilityFormValues>(
        key: K,
        value: FacilityFormValues[K]
    ) => setForm((prev) => ({ ...prev, [key]: value }));

    const updateAddress = (key: keyof AddressValues, value: string) =>
        setForm((prev) => ({
            ...prev,
            address: { ...prev.address, [key]: value },
        }));

    const toggleFeature = (feature: string) =>
        setForm((prev) => ({
            ...prev,
            featureAccess: prev.featureAccess.includes(feature)
                ? prev.featureAccess.filter((f) => f !== feature)
                : [...prev.featureAccess, feature],
        }));

    const handleSubmit = async () => {
        const missingName = !form.name.trim();
        const missingRegion = showAdminFields && !form.region.trim();
        const missingLicenses = isCreate && !form.licenses;
        const invalidPostalCode =
            isSiteAdminEdit &&
            form.address.postalCode.trim() &&
            !Number.isInteger(Number(form.address.postalCode.trim()));

        if (
            missingName ||
            missingRegion ||
            missingLicenses ||
            invalidPostalCode
        ) {
            const { title, description } = getValidationMessage(t, {
                missingName,
                invalidPostalCode,
                isCreate,
            });
            toast({ title, description, variant: 'destructive' });
            return;
        }

        if (!isCreate && !hasChanges) {
            onSaved?.();
            return;
        }

        setLoading(true);
        try {
            if (isCreate) {
                const createdFacility = await createFacility(
                    buildCreateFacilityInput(form)
                );
                toast({ title: t('facilities.editForm.facilityCreated') });
                onSaved?.(createdFacility);
            } else if (saveMode === 'siteAdmin') {
                const siteAdminUpdate = buildSiteAdminFacilityUpdate(
                    form,
                    defaults
                );

                if (siteAdminUpdate.hasChanges) {
                    await updateFacilityInfo(siteAdminUpdate.payload);
                }

                toast({ title: t('facilities.editForm.facilityUpdated') });
                onSaved?.();
            } else {
                if (!departmentId) {
                    throw new Error(
                        'Missing department id for facility update'
                    );
                }

                const updates = buildAdminFacilityUpdates(form, defaults);

                await updateFacility(departmentId, updates);

                toast({ title: t('facilities.editForm.facilityUpdated') });
                onSaved?.();
            }
        } catch {
            toast({
                title: t('facilities.editForm.error'),
                description: isCreate
                    ? t('facilities.editForm.createFailed')
                    : t('facilities.editForm.updateFailed'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="space-y-1.5">
                <Label htmlFor="facility-name">
                    {t('facilities.editForm.facilityName')}
                </Label>
                <Input
                    id="facility-name"
                    placeholder={t(
                        'facilities.editForm.facilityNamePlaceholder'
                    )}
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                />
            </div>

            {showAdminFields && (
                <div className="space-y-1.5">
                    <Label htmlFor="facility-licenses">
                        {t('facilities.editForm.licenses')}
                    </Label>
                    <Input
                        id="facility-licenses"
                        type="number"
                        placeholder={t(
                            'facilities.editForm.licensesPlaceholder'
                        )}
                        value={form.licenses || ''}
                        onChange={(e) =>
                            update(
                                'licenses',
                                parseInt(e.target.value, 10) || 0
                            )
                        }
                    />
                </div>
            )}

            {showAdminFields && (
                <div className="space-y-1.5">
                    <Label htmlFor="facility-region">
                        {t('facilities.editForm.region')}
                    </Label>
                    {lockedRegion ? (
                        <>
                            <Input
                                id="facility-region"
                                value={lockedRegion}
                                readOnly
                                disabled
                            />
                            <p className="text-xs text-muted-foreground">
                                {t('facilities.editForm.regionLocked')}
                            </p>
                        </>
                    ) : (
                        <Select
                            value={form.region || undefined}
                            onValueChange={(value) => update('region', value)}
                        >
                            <SelectTrigger id="facility-region">
                                <SelectValue
                                    placeholder={t(
                                        'facilities.editForm.selectRegion'
                                    )}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="US">US</SelectItem>
                                <SelectItem value="EU">EU</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                </div>
            )}

            {isSiteAdminEdit && (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="facility-street">
                                {t('facilities.editForm.street')}
                            </Label>
                            <Input
                                id="facility-street"
                                placeholder={t(
                                    'facilities.editForm.streetPlaceholder'
                                )}
                                value={form.address.street}
                                onChange={(e) =>
                                    updateAddress('street', e.target.value)
                                }
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="facility-number">
                                {t('facilities.editForm.number')}
                            </Label>
                            <Input
                                id="facility-number"
                                placeholder={t(
                                    'facilities.editForm.numberPlaceholder'
                                )}
                                value={form.address.number}
                                onChange={(e) =>
                                    updateAddress('number', e.target.value)
                                }
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="facility-postal">
                                {t('facilities.editForm.postalCode')}
                            </Label>
                            <Input
                                id="facility-postal"
                                placeholder={t(
                                    'facilities.editForm.postalCodePlaceholder'
                                )}
                                value={form.address.postalCode}
                                onChange={(e) =>
                                    updateAddress('postalCode', e.target.value)
                                }
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="facility-city">
                                {t('facilities.editForm.city')}
                            </Label>
                            <Input
                                id="facility-city"
                                placeholder={t(
                                    'facilities.editForm.cityPlaceholder'
                                )}
                                value={form.address.city}
                                onChange={(e) =>
                                    updateAddress('city', e.target.value)
                                }
                            />
                        </div>
                        <div className="space-y-1.5 mb-4">
                            <Label htmlFor="facility-country">
                                {t('facilities.editForm.country')}
                            </Label>
                            <Input
                                id="facility-country"
                                placeholder={t(
                                    'facilities.editForm.countryPlaceholder'
                                )}
                                value={form.address.country}
                                onChange={(e) =>
                                    updateAddress('country', e.target.value)
                                }
                            />
                        </div>
                    </div>
                </>
            )}

            {showAdminFields && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="facility-start">
                            {t('facilities.editForm.startDate')}
                        </Label>
                        <Input
                            id="facility-start"
                            type="date"
                            value={form.startDate}
                            onChange={(e) => {
                                setForm((prev) => ({
                                    ...prev,
                                    startDate: e.target.value,
                                    endDate: addDays(e.target.value, 30),
                                }));
                            }}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="facility-end">
                            {t('facilities.editForm.endDate')}
                        </Label>
                        <Input
                            id="facility-end"
                            type="date"
                            value={form.endDate}
                            onChange={(e) => update('endDate', e.target.value)}
                        />
                    </div>
                </div>
            )}

            {showAdminFields && (
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="facility-trial"
                        checked={form.isTrial}
                        onCheckedChange={(checked) =>
                            update('isTrial', checked === true)
                        }
                    />
                    <Label htmlFor="facility-trial">
                        {t('facilities.editForm.isTrial')}
                    </Label>
                </div>
            )}

            {showAdminFields && (
                <div className="space-y-2">
                    <Label>{t('facilities.editForm.featureAccess')}</Label>
                    <div className="flex flex-col gap-2">
                        {featureOptions.map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-2">
                                <Checkbox
                                    id={`facility-feature-${key}`}
                                    checked={form.featureAccess.includes(key)}
                                    onCheckedChange={() => toggleFeature(key)}
                                />
                                <Label htmlFor={`facility-feature-${key}`}>
                                    {label}
                                </Label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!hideSubmit && (
                <div className="flex justify-end">
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            loading ||
                            (!hasChanges && !allowSubmitWithoutChanges)
                        }
                    >
                        {buttonLabel}
                    </Button>
                </div>
            )}
        </div>
    );
}
